// src/encounter/intelligence/intelligence-cards.tsx
//
// The Scrips Intelligence cards, as one mount: the DoH §3.4 notice gate wrapping the Explain panel and the Next step
// card, inside an error boundary. Both encounter layouts mount THIS component (the classic drawer, encounter-drawer.tsx,
// and frame v2's own right column, encounter-shell-page.tsx), so the two cannot drift.
//
// The caller decides the engine flag (env.intelligenceEngineEnabled) and the patient; this component only draws.
// The boundary: a card that throws while rendering shows the fixed technical line and the encounter keeps working.
// Nothing logs — no error text, patient id or payload reaches a console or telemetry.
import { Component, type ReactNode } from 'react';
import { AcknowledgementGate } from './acknowledgement-gate';
import { ExplainPanel } from './explain-panel';
import { C } from './explain-card-classes';
import { STATE_LINE } from './explanation';
import { NextStepPanel } from './next-step-card';
import { useRole } from '@/auth/use-role';
import type { ExplainRequest } from './explain-panel';
import { AskPanel } from './ask-panel';
import type { AskRequest } from './ask-words';

interface BoundaryProps {
  children: ReactNode;
}

/** A render failure inside the cards becomes the technical line; it never reaches the encounter's own boundary. */
export class IntelligenceBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  // componentDidCatch is deliberately absent: nothing logs.

  render() {
    if (this.state.failed) {
      return (
        <p role="status" className={C.stateLine} data-slot="intelligence-boundary">
          {STATE_LINE.technical}
        </p>
      );
    }
    return this.props.children;
  }
}

export interface IntelligenceCardsProps {
  patientId: string;
  encounterId?: string | null;
  /**
   * Frame v2 rail (round 18r design): the cards load by themselves once the notice is confirmed — Next best action
   * first, then the Explain card for `explainRequest` — with no "Show" or "Heart & vessels" buttons. Absent: the
   * classic drawer's buttons.
   */
  explainRequest?: ExplainRequest;
  /** Frame v2 rail: "Open the results list" under the Explain card's results. */
  onOpenResults?: () => void;
  /** The clinician's Ask question (rail mode); null: no Ask card. */
  askRequest?: AskRequest | null;
  /** The visit's appointment id; Explain sends it beside the encounter id (live 4 Nov: a just-arrived encounter). */
  appointmentId?: string | null;
}

export function IntelligenceCards({ patientId, encounterId = null, explainRequest, onOpenResults, askRequest = null, appointmentId = null }: IntelligenceCardsProps) {
  const rail = explainRequest !== undefined;
  // Next best action proposes orders: a physician only, as the engine reads it (decisionsHttp.ts PRESCRIBER). Others see
  // the cards their role may see rather than a refusal (4 Nov).
  const prescriber = useRole().clinicalRole === 'physician';
  return (
    // Keyed on the patient so a patient change also clears a tripped boundary.
    <IntelligenceBoundary key={patientId}>
      {/* DoH §3.4: the AI notice gate; the cards show only after the clinician has confirmed the current notice. */}
      <AcknowledgementGate>
        {rail ? (
          // Round 18r: Next best action sits first, the Explain card below it.
          <>
            {/* Ask (4 Nov): the clinician's own question, its answer first, above the cards that load by themselves. */}
            <AskPanel key={`ask-${patientId}`} patientId={patientId} request={askRequest} />
            {prescriber ? <NextStepPanel key={`next-step-${patientId}`} patientId={patientId} encounterId={encounterId} autoLoad /> : null}
            <ExplainPanel key={patientId} patientId={patientId} encounterId={encounterId} appointmentId={appointmentId} request={explainRequest} onOpenResults={onOpenResults} />
          </>
        ) : (
          <>
            <ExplainPanel key={patientId} patientId={patientId} encounterId={encounterId} appointmentId={appointmentId} />
            {prescriber ? <NextStepPanel key={`next-step-${patientId}`} patientId={patientId} encounterId={encounterId} /> : null}
          </>
        )}
      </AcknowledgementGate>
    </IntelligenceBoundary>
  );
}
